import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import BulkUploadPopupWrapper from 'app/shared-components/BulkUploadPopupWrapper'

import useLabel from 'app/hooks/useLabel'
import SimpleBulkUpload from 'app/shared-components/SimpleBulkUpload'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useLoggerRoutes from 'app/SkyNetRoutes/hooks/useLoggerRoutes'
import {
    BatchOperationPayload,
    UpdateFormData,
} from 'app/shared-components/SimpleBulkUpload/SimpleBulkUpload.types'
import {
    BatchEntity,
} from 'app/enums/common.enums'

import loggerRequests from 'app/requests/Logger.requests'
import getColumns from './LoggerBulkUploadPopup.columns'
import useStyles from './LoggerBulkUploadPopup.styles'
import PopupWarning from './PopupWarning'
import {
    LoggerBulkUploadFormValues,
} from './LoggerBulkUploadPopup.types'
import {
    getFeedbackFormFields,
    getUpdateFormFields,
} from './LoggerBulkUploadPopup.fields'
import {
    requestMap,
} from './LoggerBulkUploadPopup.utils'

const LoggerBulkUploadPopup = () => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()

    const {
        Loggers: LoggersRoutes,
    } = useLoggerRoutes()

    const [
        updateFormValues,
        setUpdateFormValues,
    ] = useState<UpdateFormData & LoggerBulkUploadFormValues>()

    const columns = useMemo(() => {
        return getColumns({
            l,
            getLoggerLink: (loggerNumber) => {
                return loggerNumber ? generateSkyNetLink({
                    domainPath: LoggersRoutes.Overview.path,
                    params: {
                        key: loggerNumber,
                    },
                }) : null
            },
            linkStyles: classes.link,
        })
    }, [
        LoggersRoutes.Overview.path,
        classes.link,
        l,
    ])

    const feedbackFormFields = useMemo(() => {
        return getFeedbackFormFields({
            updateToType: updateFormValues?.update,
        })
    }, [updateFormValues?.update])

    const updateFormFields = useMemo(() => {
        return getUpdateFormFields({
            updateToType: updateFormValues?.update,
        })
    }, [updateFormValues?.update])

    useEffect(() => {
        setUpdateFormValues((prevValues) => {
            return {
                ...prevValues,
                updateTo: undefined,
            }
        })
    }, [updateFormValues?.update])

    const mapUploadParams = useCallback((
        data: BatchOperationPayload<LoggerBulkUploadFormValues>,
    ) => {
        return requestMap[data.update](data)
    }, [])

    const renderChildren = useCallback(({
        closePopup,
        filtersData,
    }) => {
        const handleClose = () => {
            setUpdateFormValues(undefined)
            closePopup()
        }

        return (
            <SimpleBulkUpload<LoggerBulkUploadFormValues>
                entity={BatchEntity.LOGGER}
                dataRequest={loggerRequests.filterRequestAll}
                dataRequestKey={RequestKeys.getLoggerFilterRequest}
                uploadParamsMapper={mapUploadParams}
                dataUpdateFormInfoComponent={<PopupWarning />}
                feedbackFormFields={feedbackFormFields}
                dataUpdateFormFields={updateFormFields}
                errorFeedbackColumns={columns}
                onUpdateFormChange={setUpdateFormValues}
                updateFormValue={updateFormValues}
                classNames={{
                    dataUpdateForm: {
                        gridCardWrapper: classes.updateformGridCardWrapper,
                    },
                }}
                filterParams={filtersData}
                onCancel={handleClose}
            />
        )
    }, [
        mapUploadParams,
        feedbackFormFields,
        updateFormFields,
        columns,
        updateFormValues,
        classes.updateformGridCardWrapper,
    ])

    return (
        <BulkUploadPopupWrapper
            renderChildren={renderChildren}
        />
    )
}

export default LoggerBulkUploadPopup
